import type { GccResidualWorkload as ResidualWorkload } from '@shared/types'
import { GCC_DISPOSITIONS } from '@tn/shared'
import { GccTooltip } from './GccTooltip'
import { GCC_TOOLTIPS } from './gcc-tooltips'

/** FTE with at most one decimal, without a trailing ".0". */
export function fmtFte(fte: number): string {
  return Number.isInteger(fte) ? String(fte) : fte.toFixed(1)
}

/**
 * Residual workload per capability after Step 1 (WI 2323). Every figure comes from
 * the deterministic roll-up saved with Step 1, so the table shows exactly what the
 * pipeline calculated. Designs saved before WI 2323 have no roll-up and render nothing.
 */
export default function GccResidualWorkload({ residual }: { residual?: ResidualWorkload }) {
  if (!residual || residual.capabilities.length === 0) return null
  const assumptions = GCC_DISPOSITIONS.map(d => `${d} ${residual.residualPctByDisposition[d]}%`).join(' · ')

  return (
    <section className="mb-6" aria-label="Residual workload">
      <div className="flex items-baseline justify-between mb-2">
        <GccTooltip content={GCC_TOOLTIPS.residualWorkload}>
          <h4 className="text-sm font-semibold text-gray-800">Residual workload by capability</h4>
        </GccTooltip>
        <span className="text-[10px] text-gray-400">Residual % by disposition: {assumptions}</span>
      </div>
      <div className="gx-tbl-wrap">
        <table className="gx-tbl w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-100 bg-gray-50">
              <th className="px-3 py-2 font-medium">Capability</th>
              <th className="px-3 py-2 font-medium">Function</th>
              <th className="px-3 py-2 font-medium text-right">Tasks</th>
              <th className="px-3 py-2 font-medium text-right">
                <GccTooltip content={GCC_TOOLTIPS.residualBaselineFte}>Baseline FTE</GccTooltip>
              </th>
              <th className="px-3 py-2 font-medium text-right">
                <GccTooltip content={GCC_TOOLTIPS.residualFte}>Residual FTE</GccTooltip>
              </th>
              <th className="px-3 py-2 font-medium text-right">
                <GccTooltip content={GCC_TOOLTIPS.residualPct}>Residual %</GccTooltip>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {residual.capabilities.map(c => (
              <tr key={`${c.functionScope}/${c.capabilityName}`}>
                <td className="px-3 py-1.5 font-medium text-gray-700">{c.capabilityName}</td>
                <td className="px-3 py-1.5 text-gray-500">{c.functionScope}</td>
                <td className="px-3 py-1.5 text-right text-gray-500">{c.taskCount}</td>
                <td className="px-3 py-1.5 text-right text-gray-700">{fmtFte(c.baselineFte)}</td>
                <td className="px-3 py-1.5 text-right font-semibold text-gray-900">{fmtFte(c.residualFte)}</td>
                <td className="px-3 py-1.5 text-right text-gray-500">{c.residualPct}%</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200 font-semibold text-gray-800">
              <td className="px-3 py-2" colSpan={3}>Total</td>
              <td className="px-3 py-2 text-right">{fmtFte(residual.totalBaselineFte)}</td>
              <td className="px-3 py-2 text-right">{fmtFte(residual.totalResidualFte)}</td>
              <td className="px-3 py-2 text-right">{residual.totalResidualPct}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {residual.defaultedTaskIds.length > 0 && (
        <p className="mt-2 text-[10px] text-amber-700">
          {residual.defaultedTaskIds.length} task{residual.defaultedTaskIds.length !== 1 ? 's were' : ' was'} not tagged by Step 1 and counted as Human (no reduction).
        </p>
      )}
    </section>
  )
}
